import {useCallback, useMemo, useRef, useState} from 'react';

import {MAX_GALLERY_SELECTABLE} from '~constants/constants';

import {ImageOverviewMode} from '~components/images/utils/image-mode-mixins';

interface UseCheckedItemsProps {
  allItemsIds?: string[];
  currentPageItemIds?: string[];
  mode: ImageOverviewMode;
}

interface SetCheckedOptions {
  shiftKey?: boolean;
}

export function useCheckedItems({allItemsIds, currentPageItemIds, mode}: UseCheckedItemsProps) {
  const [checkedItemIds, setCheckedItemIds] = useState<string[]>([]);
  const checkedIdsSet = useMemo(() => new Set(checkedItemIds), [checkedItemIds]);
  const [itemLimit, setItemLimit] = useState<number | null>(null);
  const lastClickedItemIdRef = useRef<string | null>(null);

  const currentPageCheckedIds = useMemo(() => {
    if (!currentPageItemIds) return [];
    return currentPageItemIds.filter((id) => checkedIdsSet.has(id));
  }, [currentPageItemIds, checkedIdsSet]);

  const isCurrentPagePartiallyChecked = useMemo(() => {
    if (!currentPageItemIds?.length) return false;
    return currentPageCheckedIds.length > 0 && currentPageCheckedIds.length < currentPageItemIds.length;
  }, [currentPageCheckedIds.length, currentPageItemIds]);

  const isCurrentPageFullyChecked = useMemo(() => {
    if (!currentPageItemIds?.length) return false;
    return currentPageItemIds.length > 0 && currentPageCheckedIds.length === currentPageItemIds.length;
  }, [currentPageCheckedIds.length, currentPageItemIds]);

  const selectableAllItemIds = useMemo(() => {
    if (!allItemsIds?.length) {
      return allItemsIds;
    }
    if (mode === 'model-finetune' && itemLimit !== null) {
      return allItemsIds.slice(0, itemLimit);
    }
    if (allItemsIds.length > MAX_GALLERY_SELECTABLE) {
      return allItemsIds.slice(0, MAX_GALLERY_SELECTABLE);
    }
    return allItemsIds;
  }, [allItemsIds, itemLimit, mode]);

  const setAllChecked = useCallback(
    (checked: boolean) => {
      const shouldLimit = mode === 'model-finetune' && itemLimit !== null;
      const itemsToCheck = shouldLimit ? selectableAllItemIds?.slice(0, itemLimit) : selectableAllItemIds;

      if (!checked) {
        lastClickedItemIdRef.current = null;
      }

      setCheckedItemIds(checked ? (itemsToCheck ?? []) : []);
    },
    [selectableAllItemIds, itemLimit, mode],
  );

  const applyItemLimit = useCallback(
    (ids: string[]) => {
      if (mode === 'model-finetune' && itemLimit !== null) {
        return ids.slice(0, itemLimit);
      }
      return ids;
    },
    [mode, itemLimit],
  );

  const setChecked = useCallback(
    (itemId: string, options?: SetCheckedOptions) => {
      const {shiftKey} = options ?? {};

      if (shiftKey && lastClickedItemIdRef.current && currentPageItemIds?.length) {
        const anchorIndex = currentPageItemIds.indexOf(lastClickedItemIdRef.current);
        const clickedIndex = currentPageItemIds.indexOf(itemId);

        if (anchorIndex !== -1 && clickedIndex !== -1) {
          const startIndex = Math.min(anchorIndex, clickedIndex);
          const endIndex = Math.max(anchorIndex, clickedIndex);
          const rangeIds = currentPageItemIds.slice(startIndex, endIndex + 1);

          setCheckedItemIds((prev) => applyItemLimit([...new Set([...prev, ...rangeIds])]));
          lastClickedItemIdRef.current = itemId;
          return;
        }
      }

      setCheckedItemIds((prev) => {
        const isChecked = prev.includes(itemId);
        const next = isChecked ? prev.filter((id) => id !== itemId) : [...prev, itemId];
        return applyItemLimit(next);
      });
      lastClickedItemIdRef.current = itemId;
    },
    [applyItemLimit, currentPageItemIds],
  );

  const setCurrentPageChecked = useCallback(
    (checked: boolean) => {
      if (!currentPageItemIds?.length) return;

      setCheckedItemIds((prev) => {
        const prevSet = new Set(prev);

        if (checked) {
          currentPageItemIds.forEach((id) => prevSet.add(id));
        } else {
          currentPageItemIds.forEach((id) => prevSet.delete(id));
        }

        return applyItemLimit(Array.from(prevSet));
      });
    },
    [applyItemLimit, currentPageItemIds],
  );

  const setMultipleChecked = useCallback(
    (itemIds: string[]) => {
      let newCheckedIds: string[] = [];

      const areAllInList = itemIds.every((id) => checkedIdsSet.has(id));

      if (areAllInList) {
        newCheckedIds = Array.from(checkedIdsSet).filter((id) => !itemIds.includes(id));
      } else {
        newCheckedIds = itemIds;
      }

      setCheckedItemIds(applyItemLimit(newCheckedIds));
    },
    [applyItemLimit, checkedIdsSet],
  );

  return {
    checkedItemIds,
    setChecked,
    setAllChecked,
    allChecked: !!selectableAllItemIds?.length && checkedItemIds.length === selectableAllItemIds.length,
    setMultipleChecked,
    currentPageCheckedIds,
    currentPageItemIds: currentPageItemIds ?? [],
    isCurrentPagePartiallyChecked,
    isCurrentPageFullyChecked,
    currentPageChecked: isCurrentPageFullyChecked,
    setCurrentPageChecked,
    itemLimit,
    setItemLimit,
  };
}
